import { useTheme } from '../../../contexts/ThemeContext';

export default function Benefits() {
  const { isDark } = useTheme();

  const benefits = [
    {
      icon: 'ri-time-line',
      title: 'Reduce Operational Workload',
      description: 'Automate repetitive tasks so your team can focus on work that actually moves the needle.',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Respond to Customers Instantly',
      description: 'AI-powered engagement ensures no lead or inquiry goes unanswered — day or night.',
    },
    {
      icon: 'ri-trophy-line',
      title: 'Capture More Opportunities',
      description: 'Intelligent systems identify and act on opportunities your team would otherwise miss.',
    },
    {
      icon: 'ri-team-line',
      title: 'Improve Team Productivity',
      description: 'Give your people better tools and fewer bottlenecks so they can perform at their best.',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Turn Data into Strategic Insights',
      description: 'Transform raw operational data into clear dashboards and actionable intelligence.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Scale Without Adding Overhead',
      description: 'Grow your operations without proportionally growing your headcount or costs.',
    },
  ];

  return (
    <>
      {/* Benefits Section */}
      <section
        className="py-32 px-8 lg:px-12 transition-colors duration-300"
        style={{ background: isDark ? '#111827' : '#F3F4F6' }}
      >
        <div className="max-w-7xl mx-auto">
          <p
            className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2563EB] mb-5 text-center"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Why Fortixa
          </p>

          <h2
            className="text-4xl lg:text-5xl font-bold text-center leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif', color: isDark ? '#F9FAFB' : '#111827' }}
          >
            Why Businesses Choose Fortixa
          </h2>

          <p
            className="text-lg text-center max-w-2xl mx-auto mb-20 leading-relaxed"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 400,
              color: isDark ? '#9CA3AF' : '#374151',
            }}
          >
            Fortixa helps businesses transition from manual operations to intelligent systems that support sustainable growth.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group rounded-2xl p-8 transition-all duration-500 border cursor-pointer"
                style={{
                  background: isDark ? '#1a1f2e' : '#ffffff',
                  borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'transparent',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = isDark ? 'rgba(37,99,235,0.3)' : '#F3F4F6';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'transparent';
                }}
              >
                <div
                  className="w-11 h-11 flex items-center justify-center rounded-xl mb-6 group-hover:bg-[#2563EB] transition-colors duration-300"
                  style={{ background: isDark ? '#374151' : '#F3F4F6' }}
                >
                  <i
                    className={`${benefit.icon} text-lg group-hover:text-white transition-colors duration-300`}
                    style={{ color: isDark ? '#9CA3AF' : '#374151' }}
                  ></i>
                </div>
                <h3
                  className="text-base font-bold mb-3 tracking-tight"
                  style={{ fontFamily: 'Montserrat, sans-serif', color: isDark ? '#F9FAFB' : '#111827' }}
                >
                  {benefit.title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 400,
                    color: isDark ? '#9CA3AF' : '#374151',
                  }}
                >
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section
        id="contact"
        className="py-32 px-8 lg:px-12"
        style={{ background: isDark ? '#0a0a0a' : '#111827' }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <p
            className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2563EB] mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Get Started
          </p>

          <h2
            className="text-4xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-8"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Strengthen Your
            <br />
            Business with AI
          </h2>

          <p
            className="text-lg text-white/60 max-w-2xl mx-auto mb-12 leading-relaxed"
            style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400 }}
          >
            Discover how intelligent automation can simplify operations, improve responsiveness, and create scalable systems for your organization.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#contact"
              className="px-8 py-4 bg-[#2563EB] text-white text-sm font-semibold rounded-full hover:bg-[#1d4ed8] transition-all shadow-lg hover:shadow-[#2563EB]/30 hover:shadow-2xl cursor-pointer whitespace-nowrap"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Schedule an AI Strategy Call
            </a>
            <a
              href="#services"
              className="px-8 py-4 bg-white/10 text-white text-sm font-semibold rounded-full hover:bg-white/20 transition-all border border-white/20 cursor-pointer whitespace-nowrap backdrop-blur-sm"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Request a System Assessment
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
